import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { DocumenteService } from './documente.service';
import { environment } from 'src/environments/environment';

interface VerificationResponse {
    success: boolean;
    message?: string;
    documentUrl?: string;
    documentName?: string;
}

@Component({
    selector: 'app-documente',
    templateUrl: './documente.component.html',
    styleUrls: ['./documente.component.css']
})
export class DocumenteComponent implements OnInit, OnDestroy {
    currentStep: number = 1;
    isTokenAccess: boolean = false;
    isLoading: boolean = false;

    // Forms
    // COMMENTED OUT - Original verification form
    // verificationForm!: FormGroup;
    otpForm!: FormGroup;

    // Data
    token: string | null = null;
    documentData: any = null;
    errorMessage: string = '';
    tipPersoana: number = 1; // 1 pentru "pf", 2 pentru "pj"
    currentChannel: string = 'sms'; // Channel curent pentru OTP

    // Timers pentru resend
    resendSmsTimer: number = 0;
    resendSmsInterval: any = null;
    resendEmailTimer: number = 0;
    resendEmailInterval: any = null;

    private destroy$ = new Subject<void>();

    // COMMENTED OUT - Original simbol filtering logic
    // filteredSimbol: any[] = [];
    // simbolControl = new FormControl();
    // simboluri: any[] = [];
    // isSimboluriLoading = false;
    // showMessage = true;
    // loadComplete = false;

    // Link-uri utile - se vor actualiza dinamic
    usefulLinks = [
        {
            title: 'Actualizare date contract - modificare date autovehicul (schimbare autovehicul)',
            url: '#',
            icon: 'directions_car'
        },
        {
            title: 'Actualizare date contract - talon auto/rca',
            url: '#',
            icon: 'description'
        },
    ];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fb: FormBuilder,
        private documenteService: DocumenteService
    ) {
        this.initializeForms();

        // COMMENTED OUT - Original simbolControl valueChanges subscription
        // this.simbolControl.valueChanges
        //     .pipe(
        //         debounceTime(600),
        //         takeUntil(this.destroy$)
        //     )
        //     .subscribe(simbol => {
        //         this.fetchSimboluri(simbol);
        //         this.showMessage = true;
        //         this.loadComplete = false;
        //     });
    }

    ngOnInit(): void {
        // Verifică dacă există token în URL
        this.route.queryParams.subscribe(params => {
            this.token = params['t'];
            if (this.token) {
                this.isTokenAccess = true;
                this.verifyToken();
            } else {
                // Dacă nu există token, nu se permite accesul
                this.isTokenAccess = false;
                this.currentStep = 1;
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.stopResendSmsTimer();
        this.stopResendEmailTimer();
    }

    private initializeForms(): void {
        // COMMENTED OUT - Original verification form
        // this.verificationForm = this.fb.group({
        //     identificator: ['', Validators.compose([
        //         Validators.required,
        //         Validators.pattern(/^\d+$/),
        //         Validators.minLength(3),
        //         Validators.maxLength(13)
        //     ])],
        //     parcare_id: ['', [Validators.required]],
        //     parcare_no: ['', Validators.compose([
        //         Validators.required,
        //         Validators.pattern(/^\d+$/)
        //     ])]
        // });

        this.otpForm = this.fb.group({
            otpCode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
            channel: [this.currentChannel, [Validators.required]],
            dataProcessingAgreement: [false, [Validators.requiredTrue]],
            privacyAgreement: [false, [Validators.requiredTrue]]
        });
    }

    // COMMENTED OUT - Original fetchSimboluri method
    // fetchSimboluri(simbol: string) {
    //     if (simbol.length >= 2) {
    //         this.isSimboluriLoading = true;
    //         this.documenteService.getSimboluri(simbol).subscribe({
    //             next: (response: any) => {
    //                 this.simboluri = response.data;
    //                 this.isSimboluriLoading = false;
    //                 this.loadComplete = true;
    //                 this.showMessage = this.simboluri.length === 0;
    //             },
    //             error: (err: any) => {
    //                 this.isSimboluriLoading = false;
    //                 this.showMessage = true;
    //                 this.loadComplete = true;
    //                 console.log(err);
    //             }
    //         });
    //     } else {
    //         this.simboluri = [];
    //         this.showMessage = true;
    //         this.loadComplete = false;
    //     }
    // }

    // COMMENTED OUT - Original onSimbolClosed method
    // onSimbolClosed() {
    //     const selectedValue = this.simbolControl.value;
    //     if (!selectedValue) {
    //         this.verificationForm.get('parcare_id')?.patchValue('');
    //     }
    // }

    async verifyToken(): Promise<void> {
        if (!this.token) return;

        this.isLoading = true;
        this.errorMessage = '';

        try {
            this.documenteService.checkToken(this.token).subscribe({
                next: (response: any) => {
                    if (response.status_code === 200 && !response.error) {
                        // Salvăm datele din response
                        this.documentData = response.data;
                        this.currentStep = 2;
                        this.startResendSmsTimer();
                    } else {
                        this.errorMessage = response.message || 'Token invalid sau expirat';
                        this.currentStep = 1;
                        this.isTokenAccess = false;
                    }
                    this.isLoading = false;
                },
                error: (error: any) => {
                    this.errorMessage = 'Eroare la verificarea tokenului';
                    this.currentStep = 1;
                    this.isTokenAccess = false;
                    this.isLoading = false;
                    console.error('Token verification error:', error);
                }
            });
        } catch (error) {
            this.errorMessage = 'Eroare la verificarea tokenului';
            this.currentStep = 1;
            this.isTokenAccess = false;
            this.isLoading = false;
        }
    }

    // COMMENTED OUT - Original submitVerification method
    // async submitVerification(): Promise<void> {
    //     if (this.verificationForm.invalid) {
    //         this.markFormGroupTouched(this.verificationForm);
    //         return;
    //     }

    //     this.isLoading = true;
    //     this.errorMessage = '';

    //     try {
    //         const formData = this.verificationForm.value;

    //         this.documenteService.checkIdentificare(formData).subscribe({
    //             next: (response: any) => {
    //                 if (response.status_code === 200 && !response.error) {
    //                     // Salvăm datele pentru următorul pas
    //                     this.documentData = response.data;
    //                     this.currentStep = 2;
    //                     this.startResendSmsTimer();

    //                     console.log('Verification successful:', response.data);
    //                 } else {
    //                     this.errorMessage = response.message || 'Datele introduse nu sunt corecte';
    //                 }
    //                 this.isLoading = false;
    //             },
    //             error: (error: any) => {
    //                 if (error.error?.message) {
    //                     this.errorMessage = error.error.message;
    //                 } else {
    //                     this.errorMessage = 'Eroare la verificarea datelor';
    //                 }
    //                 this.isLoading = false;
    //                 console.error('Verification error:', error);
    //             }
    //         });
    //     } catch (error) {
    //         this.errorMessage = 'Eroare la verificarea datelor';
    //         this.isLoading = false;
    //     }
    // }

    async submitOTP(): Promise<void> {
        if (this.otpForm.invalid) {
            this.markFormGroupTouched(this.otpForm);
            return;
        }

        this.isLoading = true;
        this.errorMessage = '';

        try {
            const otpPayload = {
                channel: this.otpForm.value.channel,
                access_token: this.documentData?.access_token, // Folosește access_token din documentData
                id: this.documentData?.id,
                code_otp: parseInt(this.otpForm.value.otpCode)
            };

            console.log('OTP Payload:', otpPayload);

            this.documenteService.confirmOtp(otpPayload).subscribe({
                next: (response: any) => {
                    if (response.status_code === 200 && !response.error) {
                        // Merge datele noi cu cele existente
                        this.documentData = { ...this.documentData, ...response.data };

                        // Salvează tipul de persoană din response
                        if (response.data && typeof response.data === 'string') {
                            this.tipPersoana = response.data === 'pf' ? 1 : 2;
                            console.log('Tip persoană salvat:', response.data, '-> tipPersoana:', this.tipPersoana);
                        }

                        // Actualizează link-urile utile cu URL-urile corecte
                        this.updateUsefulLinks();

                        // COMMENTED OUT - Original logic for non-token access
                        // Pentru token access, se merge la pasul 3 pentru descărcare
                        // Pentru access normal, se merge la pasul 3
                        // if (!this.isTokenAccess) {
                        //     this.currentStep = 3;
                        // }

                        // Pentru token access, se merge la pasul 3 pentru descărcare
                        if (this.isTokenAccess) {
                            this.currentStep = 3;
                        }

                        this.stopResendSmsTimer();
                        this.stopResendEmailTimer();
                    } else {
                        this.errorMessage = response.message || 'Codul OTP este incorect';
                    }
                    this.isLoading = false;
                },
                error: (error: any) => {
                    if (error.error?.message) {
                        this.errorMessage = error.error.message;
                    } else {
                        this.errorMessage = 'Eroare la verificarea codului OTP';
                    }
                    this.isLoading = false;
                    console.error('OTP verification error:', error);
                }
            });
        } catch (error) {
            this.errorMessage = 'Eroare la verificarea codului OTP';
            this.isLoading = false;
        }
    }

    resendOTPSms(): void {
        if (this.resendSmsTimer > 0) return;
        this.resendOTP('sms');
        this.startResendSmsTimer();
    }

    resendOTPEmail(): void {
        if (this.resendEmailTimer > 0) return;
        this.resendOTP('email');
        this.startResendEmailTimer();
    }

    private resendOTP(channel: string): void {
        this.isLoading = true;

        try {
            const resendPayload = {
                channel: channel,
                access_token: this.documentData?.access_token, // Folosește access_token din documentData
                id: this.documentData?.id
            };

            console.log('Resend OTP Payload:', resendPayload);

            this.documenteService.resendOtp(resendPayload).subscribe({
                next: (response: any) => {
                    if (response.status_code === 200 && !response.error) {
                        const channelText = channel === 'sms' ? 'SMS' : 'email';
                        console.log(`OTP retrimis cu succes prin ${channelText}`);

                        // Actualizează channel-ul curent
                        this.currentChannel = channel;
                        this.otpForm.patchValue({ channel: channel });
                    } else {
                        this.errorMessage = response.message || `Eroare la retrimisia codului prin ${channel}`;
                    }
                    this.isLoading = false;
                },
                error: (error: any) => {
                    if (error.error?.message) {
                        this.errorMessage = error.error.message;
                    } else {
                        this.errorMessage = `Eroare la retrimisia codului prin ${channel}`;
                    }
                    this.isLoading = false;
                    console.error('Resend OTP error:', error);
                }
            });
        } catch (error) {
            this.errorMessage = `Eroare la retrimisia codului prin ${channel}`;
            this.isLoading = false;
        }
    }

    downloadDocument(): void {
        if (!this.documentData?.access_token || !this.documentData?.id) {
            this.errorMessage = 'Nu s-au găsit datele necesare pentru descărcare';
            return;
        }

        this.isLoading = true;
        this.errorMessage = '';

        // Payload-ul este același ca pentru confirmOtp
        const downloadPayload = {
            channel: this.otpForm.value.channel || 'sms',
            access_token: this.documentData.access_token,
            id: this.documentData.id,
            code_otp: parseInt(this.otpForm.value.otpCode)
        };

        console.log('Download Payload:', downloadPayload);

        this.documenteService.downloadDocument(downloadPayload).subscribe({
            next: (response: Blob) => {
                // Creează un blob URL și descarcă fișierul
                const blob = new Blob([response], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `document_${this.documentData.parcare_simbol || 'contract'}_${this.documentData.parcare_no || 'parcare'}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                this.isLoading = false;
            },
            error: (error: any) => {
                this.errorMessage = 'Eroare la descărcarea documentului';
                this.isLoading = false;
                console.error('Download error:', error);
            }
        });
    }

    private updateUsefulLinks(): void {
        const baseUrl = 'https://info.mobilitateurbana4.ro/#/';

        this.usefulLinks = [
            {
                title: 'Actualizare date contract - modificare date autovehicul (schimbare autovehicul)',
                url: baseUrl + `?section=2&tip_persoana=${this.tipPersoana}&categorie=1`,
                icon: 'directions_car'
            },
            {
                title: 'Actualizare date contract - talon auto/rca',
                url: baseUrl + `?section=4&tip_persoana=${this.tipPersoana}&categorie=1`,
                icon: 'description'
            },
        ];

        console.log('Link-uri actualizate pentru tipPersoana:', this.tipPersoana, this.usefulLinks);
    }

    // COMMENTED OUT - Original goToStep method
    // goToStep(step: number): void {
    //     // Pentru token access, permite doar între pașii 1 și 2
    //     if (this.isTokenAccess) {
    //         if (step >= 1 && step <= 2) {
    //             this.currentStep = step;
    //             this.errorMessage = '';
    //         }
    //     } else {
    //         // Pentru access normal, permite navigare doar înapoi sau la pasul curent
    //         if (step <= this.currentStep) {
    //             this.currentStep = step;
    //             this.errorMessage = '';
    //         }
    //     }
    // }

    resetToStep1(): void {
        // Resetează tokenul și revine la pasul 1
        this.token = null;
        this.isTokenAccess = false;
        this.currentStep = 1;
        this.documentData = null;
        this.errorMessage = '';
        this.stopResendSmsTimer();
        this.stopResendEmailTimer();

        // Resetează formularele
        // COMMENTED OUT - Original verification form reset
        // this.verificationForm.reset();
        this.otpForm.reset();
        this.currentChannel = 'sms'; // Reset channel la SMS
        this.otpForm.patchValue({
            channel: this.currentChannel,
            dataProcessingAgreement: false,
            privacyAgreement: false
        });

        // Elimină tokenul din URL
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true
        });
    }

    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);
            control?.markAsTouched();
        });
    }

    private startResendSmsTimer(): void {
        this.resendSmsTimer = 60;
        this.resendSmsInterval = setInterval(() => {
            this.resendSmsTimer--;
            if (this.resendSmsTimer <= 0) {
                this.stopResendSmsTimer();
            }
        }, 1000);
    }

    private stopResendSmsTimer(): void {
        if (this.resendSmsInterval) {
            clearInterval(this.resendSmsInterval);
            this.resendSmsInterval = null;
        }
        this.resendSmsTimer = 0;
    }

    private startResendEmailTimer(): void {
        this.resendEmailTimer = 60;
        this.resendEmailInterval = setInterval(() => {
            this.resendEmailTimer--;
            if (this.resendEmailTimer <= 0) {
                this.stopResendEmailTimer();
            }
        }, 1000);
    }

    private stopResendEmailTimer(): void {
        if (this.resendEmailInterval) {
            clearInterval(this.resendEmailInterval);
            this.resendEmailInterval = null;
        }
        this.resendEmailTimer = 0;
    }

    // Getters pentru validare
    // COMMENTED OUT - Original verification form getters
    // get cnpCifError(): string {
    //     const control = this.verificationForm.get('identificator');
    //     if (control?.touched && control?.errors) {
    //         if (control.errors['required']) return 'CNP/CIF este obligatoriu';
    //         if (control.errors['pattern']) return 'CNP/CIF trebuie să conțină doar cifre';
    //         if (control.errors['minlength']) return 'CNP/CIF trebuie să aibă minim 3 caractere';
    //         if (control.errors['maxlength']) return 'CNP/CIF trebuie să aibă maxim 13 caractere';
    //     }
    //     return '';
    // }

    // get simbolError(): string {
    //     const control = this.verificationForm.get('parcare_id');
    //     if (control?.touched && control?.errors?.['required']) {
    //         return 'Simbolul este obligatoriu';
    //     }
    //     return '';
    // }

    // get locError(): string {
    //     const control = this.verificationForm.get('parcare_no');
    //     if (control?.touched && control?.errors) {
    //         if (control.errors['required']) return 'Locul este obligatoriu';
    //         if (control.errors['pattern']) return 'Locul trebuie să conțină doar cifre';
    //     }
    //     return '';
    // }

    get otpError(): string {
        const control = this.otpForm.get('otpCode');
        if (control?.touched && control?.errors) {
            if (control.errors['required']) return 'Codul OTP este obligatoriu';
            if (control.errors['pattern']) return 'Codul OTP trebuie să aibă 6 cifre';
        }
        return '';
    }

    getCurrentChannelText(): string {
        return this.currentChannel === 'sms' ? 'SMS' : 'E-mail';
    }

    isFormValid(): boolean {
        const otpCode = this.otpForm.get('otpCode');
        const dataProcessingAgreement = this.otpForm.get('dataProcessingAgreement');
        const privacyAgreement = this.otpForm.get('privacyAgreement');

        return !!(otpCode?.valid &&
            dataProcessingAgreement?.value &&
            privacyAgreement?.value);
    }

    // COMMENTED OUT - Original contract info getter
    // get contractInfo(): string {
    //     if (this.documentData?.parcare_simbol && this.documentData?.parcare_no) {
    //         return `${this.documentData.parcare_simbol} - Locul ${this.documentData.parcare_no}`;
    //     }
    //     return '';
    // }
}